import { useElectron } from '../services/electron/electron-provider';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:hook:use-external-links');
import type {
  ExternalLinkOptions,
  ExternalLinkResult,
} from '../services/electron/electron-provider';

/**
 * Hook for opening external links in new windows or the default browser
 */
export const useExternalLinks = () => {
  const { external, isElectronAvailable } = useElectron();

  /**
   * Open a link in a new Electron window
   * @param url - The URL to open
   * @param options - Window options (width, height, title, modal)
   * @returns Promise with result containing success status and window ID
   */
  const openInWindow = async (
    url: string,
    options?: ExternalLinkOptions
  ): Promise<ExternalLinkResult> => {
    if (!isElectronAvailable) {
      // Fallback to opening in browser if Electron is not available
      window.open(url, '_blank');
      return { success: true };
    }

    try {
      return await external.openLink(url, options);
    } catch (error) {
      logger.error('Failed to open link in window:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  };

  /**
   * Open a link in the default system browser
   * @param url - The URL to open
   * @returns Promise with result containing success status
   */
  const openInBrowser = async (url: string): Promise<{ success: boolean; error?: string }> => {
    if (!isElectronAvailable) {
      // Fallback to window.open if Electron is not available
      window.open(url, '_blank');
      return { success: true };
    }

    try {
      return await external.openInBrowser(url);
    } catch (error) {
      logger.error('Failed to open link in browser:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  };

  /**
   * Open a documentation link in a new window with appropriate sizing
   * @param url - The documentation URL to open
   * @param title - Optional title for the window
   */
  const openDocumentation = async (url: string, title?: string): Promise<ExternalLinkResult> => {
    return openInWindow(url, {
      width: 1000,
      height: 800,
      title: title || 'Documentation',
      modal: false,
    });
  };

  /**
   * Open a modal dialog with a link (useful for terms of service, privacy policy, etc.)
   * @param url - The URL to open
   * @param title - Title for the modal window
   */
  const openModal = async (url: string, title: string): Promise<ExternalLinkResult> => {
    return openInWindow(url, {
      width: 800,
      height: 600,
      title,
      modal: true,
    });
  };

  return {
    openInWindow,
    openInBrowser,
    openDocumentation,
    openModal,
    isElectronAvailable,
  };
};

export type { ExternalLinkOptions, ExternalLinkResult };
